import { formatInTimeZone, fromZonedTime, toZonedTime } from "date-fns-tz";

/**
 * Date/time helpers anchored on the dispatch's IANA timezone.
 *
 * Bug #3 of the pickup work: the customer picks "May 20" in the picker
 * (driver's local day), but their browser is in a different zone, so the
 * raw `new Date("2026-05-20")` round-trip drifts to May 19 once it lands on
 * the order page. All customer-facing pickup dates flow through these
 * helpers using `dispatchTimeZone` from the quote/booking response.
 *
 * Callers pass either bare `YYYY-MM-DD` (the picker's input value) or a
 * full ISO timestamp (`scheduledDate` from server responses). Normalize
 * to the date portion before composing — concatenating `T12:00:00` onto
 * a full timestamp produces `…ZT12:00:00` and `date-fns-tz` throws
 * `RangeError: Invalid time value`.
 */
function toDateOnly(iso: string): string {
  return iso.slice(0, 10);
}

function isZhLocale(locale?: string): boolean {
  return locale?.toLowerCase().startsWith("zh") ?? false;
}

function zhDateOptions(
  pattern: string,
  timeZone: string
): Intl.DateTimeFormatOptions {
  return {
    timeZone,
    ...(pattern.includes("EEE")
      ? { weekday: pattern.includes("EEEE") ? "long" : "short" }
      : {}),
    ...(pattern.includes("MMM")
      ? { month: pattern.includes("MMMM") ? "long" : "short" }
      : {}),
    ...(pattern.includes("d") ? { day: "numeric" } : {}),
    ...(pattern.includes("yyyy") ? { year: "numeric" } : {}),
  };
}

/** "2026-05-20" in the dispatch's local zone. */
export function todayIsoInTz(timeZone: string): string {
  return formatInTimeZone(new Date(), timeZone, "yyyy-MM-dd");
}

/** Offset today by N days in dispatch-local terms, return YYYY-MM-DD. */
export function todayPlusDaysIsoInTz(timeZone: string, days: number): string {
  const todayLocal = toZonedTime(new Date(), timeZone);
  todayLocal.setDate(todayLocal.getDate() + days);
  return formatInTimeZone(
    fromZonedTime(todayLocal, timeZone),
    timeZone,
    "yyyy-MM-dd"
  );
}

/** Format YYYY-MM-DD anchored on dispatch tz — no drift. */
export function formatDateInTz(
  isoDate: string,
  timeZone: string | null,
  pattern = "EEE, MMM d",
  locale?: string
): string {
  if (!timeZone) return toDateOnly(isoDate);
  const date = fromZonedTime(`${toDateOnly(isoDate)}T12:00:00`, timeZone);
  if (isZhLocale(locale)) {
    return new Intl.DateTimeFormat(
      "zh-CN",
      zhDateOptions(pattern, timeZone)
    ).format(date);
  }
  return formatInTimeZone(date, timeZone, pattern);
}

/** "9AM – 11AM EDT" (tz abbreviation from the dispatch). */
export function formatSlotRangeInTz(
  slot: { startTime: string | null; endTime: string | null } | null,
  timeZone: string | null,
  isoDate?: string,
  locale?: string
): string {
  if (!slot?.startTime || !slot.endTime || !timeZone)
    return isZhLocale(locale)
      ? "已接受的取件时段未知"
      : "Accepted timing unknown";
  const fmt = (t: string) => {
    const [h, m] = t.split(":").map(Number);
    if (isZhLocale(locale)) {
      return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    }
    const period = h >= 12 ? "PM" : "AM";
    const dh = h % 12 || 12;
    return m === 0
      ? `${dh}${period}`
      : `${dh}:${m.toString().padStart(2, "0")}${period}`;
  };
  const anchorDate = isoDate ? toDateOnly(isoDate) : todayIsoInTz(timeZone);
  const tzAbbr = formatInTimeZone(
    fromZonedTime(`${anchorDate}T12:00:00`, timeZone),
    timeZone,
    "zzz"
  );
  return `${fmt(slot.startTime)} – ${fmt(slot.endTime)} ${tzAbbr}`;
}

/** UTC instant of the slot's start on `isoDate` in `timeZone`. */
export function slotStartInstant(
  isoDate: string,
  startTime: string,
  timeZone: string
): Date {
  return fromZonedTime(`${toDateOnly(isoDate)}T${startTime}:00`, timeZone);
}

/**
 * Slot is bookable when `now + leadMinutes <= slotStart`. Mirrors the
 * server-side guard in customer-create-pickup-booking.service.ts so the UI
 * doesn't expose options the server will reject.
 */
export function isSlotBookable(
  isoDate: string,
  slot: { startTime: string; bookingLeadMinutes: number },
  timeZone: string,
  now: Date = new Date()
): boolean {
  const startMs = slotStartInstant(isoDate, slot.startTime, timeZone).getTime();
  const cutoffMs = now.getTime() + slot.bookingLeadMinutes * 60_000;
  return cutoffMs <= startMs;
}
